if (!customElements.get('localization-form')) {
  customElements.define(
    'localization-form',
    class LocalizationForm extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          input: this.querySelector('input[name="locale_code"], input[name="country_code"]'),
          button: this.querySelector('button'),
          panel: this.querySelector('.disclosure__list-wrapper'),
        };
        // touchend fires first on a touch device; calling preventDefault()
        // on it is the standard, documented way to stop the browser from
        // also firing its trailing compatibility `click` afterward, which
        // is what caused a single iPhone tap to toggle the panel twice
        // ("closes, then immediately reopens"). The separate click listener
        // still handles mouse/keyboard activation, where no touchend fires.
        this.elements.button.addEventListener('touchend', this.onButtonTouchEnd.bind(this));
        this.elements.button.addEventListener('click', this.onButtonClick.bind(this));
        // Desktop keyboard users tabbing away: relatedTarget is reliable here.
        this.elements.button.addEventListener('focusout', this.closeSelector.bind(this));
        this.addEventListener('keyup', this.onContainerKeyUp.bind(this));
        this.onDocumentClick = this.onDocumentClick.bind(this);

        this.querySelectorAll('a').forEach((item) => item.addEventListener('click', this.onItemClick.bind(this)));
      }

      connectedCallback() {
        // iOS Safari doesn't reliably keep focus on a tapped <button>, and
        // WebKit has long left `focusout`'s relatedTarget unpopulated, so
        // closeSelector() below never fires there. A document-level
        // listener closes the panel on outside taps regardless of focus
        // behavior, backstopping the focusout handler on that browser.
        //
        // It listens for pointerdown rather than click: iOS Safari only
        // dispatches a synthetic `click` for a tap when the tapped element
        // is "clickable" (a link, button, form control, or something with
        // its own click handler / cursor:pointer). A tap on plain page
        // content -- text, an image, a bare div -- often never produces a
        // click event there at all, so a click-only listener can miss most
        // outside taps on an iPhone. Pointer events don't have that
        // restriction and, unlike a raw touchstart listener, don't risk
        // reintroducing iOS's legacy ~300ms "ghost click" delay/duplicate
        // click behavior on the button itself.
        document.addEventListener('pointerdown', this.onDocumentClick);
        document.addEventListener('click', this.onDocumentClick);
      }

      disconnectedCallback() {
        document.removeEventListener('pointerdown', this.onDocumentClick);
        document.removeEventListener('click', this.onDocumentClick);
      }

      isOpen() {
        return !this.elements.panel.hasAttribute('hidden');
      }

      hidePanel() {
        if (!this.isOpen()) return;
        this.elements.button.setAttribute('aria-expanded', 'false');
        this.elements.panel.setAttribute('hidden', true);
      }

      showPanel() {
        if (this.isOpen()) return;
        this.elements.button.setAttribute('aria-expanded', 'true');
        this.elements.panel.removeAttribute('hidden');
      }

      onButtonTouchEnd(event) {
        event.preventDefault();
        this.toggle();
      }

      onButtonClick(event) {
        // Determining the next state from the panel's actual hidden
        // attribute (rather than toggling blindly off aria-expanded, as
        // before) means a click always does what it visibly should, even
        // if a prior close happened through a path that only touched one
        // of the two attributes.
        event.stopPropagation();
        this.toggle();
      }

      toggle() {
        if (this.isOpen()) {
          this.hidePanel();
        } else {
          this.showPanel();
          // preventScroll matters here: iOS Safari will otherwise scroll
          // the button into view when it gains focus, shifting the layout
          // right after opening. A second tap aimed at the same screen
          // position can then land on whatever ended up there instead of
          // this button, making it look like the panel "won't close."
          requestAnimationFrame(() => this.elements.button.focus({ preventScroll: true }));
        }
      }

      onDocumentClick(event) {
        if (this.isOpen() && !this.contains(event.target)) {
          this.hidePanel();
        }
      }

      onContainerKeyUp(event) {
        if (event.code.toUpperCase() !== 'ESCAPE') return;

        if (!this.isOpen()) return;
        this.hidePanel();
        event.stopPropagation();
        this.elements.button.focus();
      }

      onItemClick(event) {
        event.preventDefault();
        const form = this.querySelector('form');
        this.elements.input.value = event.currentTarget.dataset.value;
        if (form) form.submit();
      }

      closeSelector(event) {
        const isChild =
          this.elements.panel.contains(event.relatedTarget) || this.elements.button.contains(event.relatedTarget);
        if (!event.relatedTarget || !isChild) {
          this.hidePanel();
        }
      }
    }
  );
}
